"use client";

import { Plus, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const FEATURED_VIDEO = "j66ThqBM-vw";
export default function CosmicDialogue() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="1479" id="cosmic-dialogue" className="relative overflow-hidden bg-background py-24 md:py-36 text-foreground">
      {/* Top accent bar */}
      <div className="absolute inset-x-0 top-0 h-[6px] bg-chart-1" />
      <div className="absolute top-[6px] left-1/2 -translate-x-1/2 flex gap-2">
        <span className="block size-2 rounded-lg bg-chart-4" />
        <span className="block size-2 rounded-lg bg-chart-4" />
        <span className="block size-2 rounded-lg bg-chart-4" />
      </div>

      {/* Noise overlay */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />

      <div className="relative mx-auto max-w-[1360px] px-6 md:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* LEFT — FEATURED ASTROMOTO VIDEO */}
          <div ref={leftRef} className={`order-2 lg:order-1 lg:col-span-7 flex items-center justify-center transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="w-full max-w-3xl aspect-video rounded-lg overflow-hidden border border-border shadow-lg relative">

              <div className="absolute top-3 left-3 z-10 text-xs font-mono bg-black/60 px-2 py-1 rounded text-white tracking-wider">
                ASTROMOTO FEATURED VIDEO
              </div>

              <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${FEATURED_VIDEO}?controls=1&modestbranding=1&rel=0`} title="AstroMoto featured video" frameBorder="0" allow="encrypted-media; picture-in-picture" allowFullScreen />

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20" />
            </div>
          </div>

          {/* RIGHT — MAIN CONTENT */}
          <div ref={rightRef} className={`order-1 lg:order-2 lg:col-span-5 transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-28">

              <div className="inline-flex items-center gap-2 -rotate-3 rounded-md bg-chart-1 px-4 py-2 shadow-md">
                <span className="size-2 rounded-lg bg-background" />
                <span className="font-mono text-xs font-medium uppercase tracking-[0.15em] text-background">Transmission Received</span>
              </div>

              <h2 className="mt-8 font-default text-foreground font-bold leading-[1.05] tracking-tighter text-4xl md:text-5xl lg:text-6xl">Astro<span className="relative inline-block italic text-chart-2" data-ast-id="f:/src/pages/Observatory/sections/CosmicDialogue.tsx|sig:7ozejv" data-ast-start="3834" data-ast-end="4475">Moto<svg aria-hidden="true" viewBox="0 0 200 14" className="absolute -bottom-2 left-0 w-full h-3" preserveAspectRatio="none" data-ast-id="f:/src/pages/Observatory/sections/CosmicDialogue.tsx|sig:hppcdk" data-ast-start="3936" data-ast-end="4451"><path d="M2 7 Q 25 1, 50 7 T 100 7 T 150 7 T 198 7" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" data-ast-id="f:/src/pages/Observatory/sections/CosmicDialogue.tsx|sig:9s02vl" data-ast-start="4170" data-ast-end="4426"></path></svg></span></h2>

              <p className="mt-8 font-default text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">
                AstroMoto is a mobile astrophotography project documenting deep-sky imaging
                and night-sky exploration from a motorcycle-based observatory setup. Follow
                field sessions, gear testing, and real-world captures as portable astronomy
                is taken into remote and dynamic environments under the night sky.
              </p>

              {/* CTA — YouTube Link */}
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a href="https://www.youtube.com/@AstroMotoADV" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-md px-7 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white shadow-md transition !bg-[rgba(157,14,14,1)] hover:scale-[1.02] hover:bg-[#e60000]">
                  <Youtube className="size-4" />
                  Watch AstroMoto on YouTube
                </a>
              </div>

              {/* decorative starburst */}
              <div className="hidden lg:block mt-16 relative h-24 w-24 opacity-40">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {[...Array(7)].map((_, i) => <line key={i} data-index={i} x1="50" y1="50" x2={50 + 40 * Math.cos(i * Math.PI / 7)} y2={50 - 40 * Math.sin(i * Math.PI / 7)} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-chart-2" />)}
                </svg>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}